import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { FiEye } from "react-icons/fi";
import { TailSpin, ThreeDots } from "react-loader-spinner";
import clsx from "clsx";

import { displayFormatDate } from "../../../utils/helpers";
import {
  API_GET_TIMESHEETS_PENDING_SUBMITTED,
  GET_WFH_TIMESHEETS,
} from "../../../config/Endpoints";
import approvalStatusOptions from "../../../options/approvalStatusOptions";
import NoRecordsAvailable from "../../../components/NoRecordsAvailable";
import PageShell from "../../../v2/components/shared/page-shell/page-shell.tsx";
import {
  Badge,
  Button,
  ResponsiveDataTable,
} from "../../../v2/components/ui/index.ts";
import { showError } from "../../../v2/utils/toast.ts";
import WFHTimesheetManagementViewModal from "../WFHTimesheetManagementViewModal";

function getApiErrorMessage(error, fallback = "Something went wrong. Please try again.") {
  const msg = error?.response?.data?.message;
  if (typeof msg === "string" && msg.trim() !== "") return msg;
  if (typeof error?.message === "string" && error.message.trim() !== "")
    return error.message;
  return fallback;
}

function formatDurationMinutes(minutes) {
  const m = Number(minutes) || 0;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${h}h : ${r}m`;
}

const PendingApprovals = ({ title, description }) => {
  const accessToken = Cookies.get("accessToken");
  const [rawRows, setRawRows] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [timesheet, setTimesheet] = useState({});
  const [openModal, setOpenModal] = useState(false);

  const fetchList = useCallback(async () => {
    setTableLoading(true);
    try {
      const res = await axios.get(API_GET_TIMESHEETS_PENDING_SUBMITTED, {
        params: { page: currentPage - 1, size: limit },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = res.data;
      const items = Array.isArray(data) ? data : data?.content ?? [];
      const total =
        typeof data?.totalElements === "number"
          ? data.totalElements
          : items.length;
      setRawRows(items);
      setTotalElements(total);
    } catch (err) {
      setRawRows([]);
      setTotalElements(0);
      showError(getApiErrorMessage(err, "Failed to load pending approvals."));
    } finally {
      setTableLoading(false);
      setInitialLoading(false);
    }
  }, [accessToken, currentPage, limit]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const mapped = rawRows.map((item) => ({
      employeeName: item.employeeName ?? "-",
      displayDate: displayFormatDate(item.date),
      date: item.date,
      sheetCategory: item.sheetCategory ?? "-",
      totalTime: formatDurationMinutes(item.totalDurationMinutes),
      status: item.managerStatus,
      _original: item,
    }));
    setTableData(mapped);
  }, [rawRows]);

  const handleView = useCallback(
    async (item) => {
      setViewLoading(true);
      try {
        const res = await axios.get(GET_WFH_TIMESHEETS, {
          params: { id: item.id },
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setTimesheet(res.data?.[0] || {});
        setOpenModal(true);
      } catch (err) {
        setTimesheet({});
        showError(getApiErrorMessage(err, "Unable to load timesheet details."));
      } finally {
        setViewLoading(false);
      }
    },
    [accessToken],
  );

  const variantMap = useMemo(
    () => ({
      success: "success",
      warning: "warning",
      danger: "danger",
    }),
    [],
  );

  const columns = useMemo(
    () => [
      {
        id: "employeeName",
        header: "Employee Name",
        accessor: "employeeName",
        sortable: true,
        truncate: true,
        getTitleText: (row) => String(row.employeeName ?? ""),
      },
      {
        id: "displayDate",
        header: "Date",
        accessor: "displayDate",
        sortable: true,
        getSortValue: (row) => row.date,
      },


      {
        id: "status",
        header: "Status",
        accessor: "status",
        sortable: true,
        render: (row) => {
          const status = approvalStatusOptions.find(
            (s) => s.value === row.status,
          );
          return (
            <Badge variant={variantMap[status?.type] ?? "neutral"}>
              {status?.label || row.status || "-"}
            </Badge>
          );
        },
      },
      {
        id: "action",
        header: "Action",
        sortable: false,
        nowrap: true,
        render: (row) => (
          <Button
            type="button"
            variant="accent-ghost"
            size="sm"
            startIcon={<FiEye size={14} aria-hidden />}
            onClick={() => handleView(row._original)}
          >
            View
          </Button>
        ),
      },
    ],
    [handleView, variantMap],
  );

  return (
    <PageShell>
      {(initialLoading && tableData.length === 0) || viewLoading ? (
        <div className="overlay">
          <div className="mt-5">
            <TailSpin
              height="100"
              width="100"
              radius="1"
              color="blue"
              ariaLabel="loading"
              visible
            />
          </div>
        </div>
      ) : null}

      {(title || description) && (
        <div className="mb-3">
          {title ? (
            <h1 className={clsx("heading-md", "mb-1")}>{title}</h1>
          ) : null}
          {description ? (
            <p className={clsx("body-sm", "text-muted", "mb-0")}>{description}</p>
          ) : null}
        </div>
      )}


      {tableLoading && !initialLoading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <ThreeDots
            height="80"
            width="80"
            color="var(--color-accent-500)"
            visible
          />
        </div>
      ) : tableData.length > 0 ? (
        <ResponsiveDataTable
          columns={columns}
          data={tableData}
          aria-label="Pending submitted timesheets"
          serialNumber
          page={currentPage}
          pageSize={limit}
          totalItems={totalElements}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setLimit(size);
            setCurrentPage(1);
          }}
          pageSizeOptions={[5, 10, 20, 30, 40]}
          totalLabel={
            <>
              Total Requests: <strong>{totalElements}</strong>
            </>
          }
        />
      ) : !initialLoading ? (
        <NoRecordsAvailable />
      ) : null}


      <WFHTimesheetManagementViewModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        timesheet={timesheet}
      />
    </PageShell>
  );
};

export default PendingApprovals;
